import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';

import { NgSelectComponent } from '@ng-select/ng-select';
import { AddTagFn } from '@ng-select/ng-select/lib/ng-select.component';
import { uniqueId } from 'lodash';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, map, skip, startWith, takeUntil } from 'rxjs/operators';

import { UserpilotService } from '@app/src/app/shared/services/userpilot';

import { NgSelectModified } from '@shared/components/ng-select-modified';
import { MediaService } from '@shared/services/media';

import { FormSelectMessages } from '../../constants';
import { FormLabelBackground, FormSelectAppearance, FormSelectOption, FormSelectReturnTypes } from '../../types';
import { FormSheetSelectService } from '../form-sheet-select';

@Component({
  selector: 'mg-form-select',
  templateUrl: './form-select.component.html',
  styleUrls: ['./form-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormSelectComponent extends NgSelectModified implements OnChanges, OnDestroy, OnInit {
  // Child components

  @ViewChild(NgSelectComponent, { static: false })
  ngSelect: NgSelectComponent;

  @ContentChild(TemplateRef)
  public selectContent: TemplateRef<any>;

  // Constants

  public readonly MESSAGES = FormSelectMessages;

  // General observables

  private readonly _destroyed$ = new ReplaySubject<void>(1);
  public readonly input$ = new Subject<string>();
  private readonly _isLoading$ = new BehaviorSubject<boolean>(false);
  public readonly isLoadingObs$ = this._isLoading$.asObservable();

  // Inputs

  @Input() name: string = uniqueId('mg-select');
  @Input() appearance: FormSelectAppearance;
  @Input() addTag: boolean | AddTagFn = false;
  @Input() addTagText = 'Add item';
  @Input() returnMode: FormSelectReturnTypes = 'value';
  @Input() searchable: boolean;
  @Input() isClearable: boolean;
  @Input() options: FormSelectOption[];
  @Input() closeOnSelect = true;
  @Input() hideSelected: boolean;
  @Input() isFullWidth = false;
  @Input() labelBackground: FormLabelBackground = 'white';
  @Input() notFoundText: string = FormSelectMessages.NO_RESULTS;
  @Input() disabled: boolean;
  @Input() appendTo: string;
  @Input() condensed: boolean;
  @Input() hint: string;
  @Input() showTooltip = false;
  /**
   * Unique id for things like analytics and testing to hook into
   * Important to note changing this could break either of those
   */
  @Input() id: string;
  @Input() enableMobileSheet = false;
  @Input() setFocus: Observable<any>;
  /**
   * Hides the error message that is displayed when the input is invalid
   */
  @Input() suppressErrorMessage = false;
  /**
   * Show a loading spinner in the select
   */
  @Input() loading: boolean;
  /**
   * With multi select, this determines how many pills to show before showing the "x more" pill
   * Explicitly setting to `null` means no limit
   */
  @Input() selectedItemsToShow: number | null = 1;

  // Outputs

  @Output()
  public readonly selectionChanged = new EventEmitter<FormSelectOption | FormSelectOption[]>();
  @Output() public readonly blurEvent = new EventEmitter<void>();

  // Host bindings

  @HostBinding('class.add-mobile-bottomsheet')
  get hasCustomClass() {
    return this.enableMobileSheet;
  }

  public readonly showBottomSheetTrigger$ = this.media.isMobileView$.pipe(
    takeUntil(this._destroyed$),
    map(isMobileView => this.enableMobileSheet && isMobileView),
  );

  /** Select */
  private readonly _showClearSubject = new BehaviorSubject<boolean>(false);
  public readonly showClear$ = this._showClearSubject.asObservable();
  private _valueChanges$: Observable<any>;
  private _inputAttrs: Partial<HTMLInputElement> = {
    autocomplete: 'off',
    name: uniqueId('mg-select'),
  };
  public readonly inputAttrs = this._inputAttrs as Record<string, string>;

  get currentValue() {
    return this.value ?? this.control.value;
  }

  // Computed getters

  get searchInput$() {
    return this.input$;
  }

  get isLoading$() {
    return this._isLoading$;
  }

  get valueChanges$() {
    return this.control.valueChanges;
  }

  get getSelectedItemsToShow(): number {
    return this.selectedItemsToShow ?? 99;
  }

  /** Component constructor */
  constructor(
    public media: MediaService,
    protected _fb: UntypedFormBuilder,
    private _cdr: ChangeDetectorRef,
    private _formSheetSelect: FormSheetSelectService,
    private _userpilot: UserpilotService,
  ) {
    super(_fb);
  }

  ngOnInit(): void {
    this._valueChanges$ = this.control.valueChanges.pipe(takeUntil(this._destroyed$), distinctUntilChanged());

    if (this.id) {
      this._inputAttrs['data-test'] = this.id;
    }

    if (this.value) {
      if (Array.isArray(this.value) && !this.value.length) return;
      this._showClearSubject.next(true);
    }

    this._valueChanges$
      .pipe(
        startWith(false),
        skip(1),
        takeUntil(this._destroyed$),
        map(v => {
          return v && (Array.isArray(v) ? v[0] !== undefined : true);
        }),
      )
      .subscribe(v => {
        this._showClearSubject.next(v);
      });

    // we need to trigger change detection when the status changes
    // or the component wont update if the control is touched/dirty/etc
    // when manually marked from parent component
    this.control.statusChanges.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this._cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this._showClearSubject.complete();
  }

  /**
   * handles form control disabled state changes after Angular 16 migration
   * related to changes in Angular's input binding timing
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      if (changes['disabled'].currentValue) {
        this.control.disable();
      } else {
        this.control.enable();
      }
    }

    if (changes?.value) {
      this.control.setValue(changes.value.currentValue);
    }
  }

  public focus() {
    this.ngSelect.focus();
  }

  public open() {
    this._userpilot.track(this.id);
    this.ngSelect.open();
  }

  public async openMobileBottomSheet() {
    this._userpilot.track(this.id);
    const initialSelectionValue = this.control?.value ?? this.value;
    let initialSelectionOption: FormSelectOption<any>[];
    if (!this.multiple) {
      initialSelectionOption = this.options.filter(o => o.value === initialSelectionValue);
    } else {
      initialSelectionOption = this.options.filter(o => initialSelectionValue?.includes(o?.value));
    }
    this._formSheetSelect
      .open({
        options: this.options,
        enableSearch: this.searchable,
        title: this.placeholder,
        multiple: this.multiple,
        initialSelection: initialSelectionOption,
      })
      .subscribe(response => {
        if (response?.type === 'submit') {
          let v;
          if (this.multiple) {
            v = response?.data?.selection?.map(option => option.value);
            this.handleModelChange(v);
          } else {
            v = response?.data?.selection[0]?.value ?? null;
            const selectedOption = this.options.find(option => option.value === v);
            this.handleModelChange(selectedOption);
          }
          this.control.setValue(v);
          this._cdr.markForCheck();
        }
      });
  }

  public handleModelChange(event: FormSelectOption | FormSelectOption[]) {
    let changedResult = event;
    if (Array.isArray(changedResult)) {
      if (this.returnMode !== 'full') {
        changedResult = changedResult.map(option => option[this.returnMode]);
      }
    } else if (this.returnMode !== 'full' && !!event) {
      changedResult = changedResult[this.returnMode];
    }
    this.selectionChanged.emit(changedResult);

    // underlying ng-select has a bug where if it's searchable it wont blur on select
    // so we need to manually blur it
    if (this.searchable) {
      this.ngSelect.blur();
    }
  }

  public async setInitialValue<T>(val: T | T[]) {
    this.control.reset({
      selected: val,
    });
  }

  public async removeItem(item: any): Promise<void> {
    const val = [...this.control.value];
    val.splice(val.indexOf(item.value), 1);
    this.control.patchValue(val);
  }

  refreshSelectMenu() {
    this._cdr.markForCheck();
  }

  public resetFormSelect() {
    this.ngSelect.clearModel();
  }
}
